import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { h } from 'vue';

import forgotPasswordEvents from '@/Events/ForgotPassword';
import { useAuthStore } from '../store/AuthStore';
import { useUserStore } from '../store/UserStore';
import OfferStatus from '../views/Offer-Status.vue';
import FaqComponent from '../views/FAQ-Component.vue';
import EarnComponent from '../views/Earn-Component.vue';
import HomeComponent from '../views/Home-Component.vue';
import MyEarning from "../views/MyEarning-Component.vue";
import OfferComponent from '../views/Offer-Component.vue';
import RewardComponent from '../views/Reward-Component.vue';
import ProfileComponent from '../views/Profile-Component.vue';
import RewardsComponent from '../views/Rewards-Component.vue';
import ReferralComponent from '../views/Referral-Component.vue';
import CookiePolicy from "@/components/Common/Cookie-Policy.vue";
import MyRewardsComponent from '../views/MyRewards-Component.vue';
import RedirectToFaq from "@/components/Common/RedirectToFaq.vue";
import MyReferralComponent from '../views/MyReferral-Component.vue';
import ProfileEditComponent from '../views/Profile-Edit-Component.vue';
import PayoutOption from '../components/Rewards/Common/Payout-Option.vue';
import PaypalPayoutOption from '../components/Rewards/Paypal/Paypal-Payout-Option.vue';
import authEvents from '@/Events/AuthDialog';
import LeaderboardComponent from '@/views/Leaderboard-Component.vue';
import PolicyPage from '@/components/Common/PolicyPage.vue';


const routes: Array<RouteRecordRaw> = [
    {
        path: "/offers",
        name: "Offers",
        component: OfferComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/offers/:offerId",
        name: "OfferDetails",
        component: OfferComponent,
        meta: { requiresAuth: true },
        props: true,
    },
    {
        path: "/rewards",
        name: "Rewards",
        component: RewardsComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/reward",
        name: "Reward",
        component: RewardComponent,
        meta: { requiresAuth: true },
        children: [
            {
                path: ":id(\\d+)",
                name: "Payout Option",
                components: {
                    main: PayoutOption,
                },
            },
            {
                path: "paypal/:id(\\d+)",
                name: "Paypal Payout",
                components: {
                    main: PaypalPayoutOption,
                },
            },
        ],
    },
    {
        path: "/reward/myRewardsList",
        name: "My Rewards",
        component: MyRewardsComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/",
        name: "Home",
        component: HomeComponent,
    },
    {
        path: "/earn",
        name: "Earn",
        component: EarnComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/referrals",
        name: "Referrals",
        component: ReferralComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/myreferrals",
        name: "My Referrals",
        component: MyReferralComponent,
        meta: { requiresAuth: true },
    },

    {
        path: "/profile",
        name: "Profile",
        component: ProfileComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/edit-profile",
        name: "Edit Profile",
        component: ProfileEditComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/faq",
        name: "FAQ",
        component: PolicyPage,
        props: { type: 'faq' },
        alias: ['/faqs']
    },
    {
        path: "/terms",
        name: "Terms Of Service",
        component: PolicyPage,
        props: { type: 'terms' }
    },
    {
        path: "/privacy",
        name: "Privacy Policy",
        component: PolicyPage,
        props: { type: 'privacy' }
    },
    {
        path: "/tos",
        name: "Terms Of Service Vue",
        component: PolicyPage,
        props: { type: 'terms' },
        alias: ['/terms-of-service']
    },
    {
        path: "/policy",
        name: "Privacy Policy Vue",
        component: PolicyPage,
        props: { type: 'privacy' }
    },
    {
        path: "/cookie",
        name: "Cookie Policy",
        component: PolicyPage,
        props: { type: 'cookie' }
    },
    {
        path: '/status/:status',
        name: 'Offer Status',
        component: OfferStatus,
        meta: { requiresAuth: true }
    },
    {
        path: "/myearning",
        name: "My Earnings",
        component: MyEarning,
        meta: { requiresAuth: true },
    },
    {
        path: '/referral/:referralCode',
        name: 'Referral',
        component: HomeComponent,
        meta: { requiresAuth: true },
        props: true,
    },
    {
        path: '/reset-password/:resetCode',
        name: 'ResetPassword',
        component: HomeComponent,
        meta: { requiresAuth: false },
        beforeEnter: (to, from, next) => {
            const resetCode = to.params.resetCode;
            if (typeof resetCode === 'string') {
                forgotPasswordEvents.emit('forgot-password-email-link-received', resetCode);
            }
            next();
        }
    },
    {
        path: '/verify-email/:verificationCode',
        name: 'VerifyEmail',
        component: HomeComponent,
        meta: { requiresAuth: false },
        beforeEnter: (to, from, next) => {
            const verificationCode = to.params.verificationCode;
            if (typeof verificationCode === 'string') {
                authEvents.emit('signup-verification-email-link-received', verificationCode);
            }
            next();
        }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        redirect: { name: 'Home' }
    },
    {
        path: "/applinks",
        redirect: { name: 'Home' },
    },
    {
        path: "/leaderboard",
        name: "leaderboard",
        component: LeaderboardComponent,
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

/* global gtag */
declare global {
    interface Window {
        dataLayer: any[];
    }
}
router.afterEach((to) => {
    const authStore = useAuthStore();
    const userStore = useUserStore();
    
    // Send page view event to Google Analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'pageview',
        page: {
            path: to.path,
            title: capitalizeFirstLetter(to.name?.toString() || ''),
            query: to.query,
            params: to.params,
            user_id: userStore.user?.id || null
        }
    });
});

const DEFAULT_TITLE = "Bounty Hunter: Play Games & Get Real Rewards";

router.beforeEach((to, from, next) => {
    document.title = to.name
        ? DEFAULT_TITLE + " | " + capitalizeFirstLetter(to.name as string)
        : DEFAULT_TITLE;
    next();
});

router.beforeEach((to, from, next) => {
    const referralCode = to.params.referralCode;
    if (referralCode && typeof referralCode === 'string') {
        window.sessionStorage.setItem("referralCode", referralCode);
    }
    next();
});

router.beforeEach((to, from, next) => {
    const authStore = useAuthStore();

    // If the route requires authentication and the user is not authenticated, redirect to login
    if (to.meta.requiresAuth && !authStore.isUserLoggedIn()) {
        // Store the intended destination for redirect after login
        if (to.name === 'OfferDetails' && to.params.offerId) {
            // Store the offer ID to redirect after login
            sessionStorage.setItem('redirectOfferId', to.params.offerId.toString());
            sessionStorage.setItem('redirectPath', to.path);
        } else if (to.query.offerId) {
            // Store the offer ID from query params
            sessionStorage.setItem('redirectOfferId', to.query.offerId.toString());
            sessionStorage.setItem('redirectPath', to.path);
        }
        
        authStore.setLoginDialogVisible(true);
        next(false);
    } else {
        next();
    }
});


router.beforeEach((to, from, next) => {
    // Scroll to the top of the page on route change
    window.scrollTo(0, 0);
    next();
});

function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export default router;
